// ===========================================
import { useTranslation } from 'react-i18next';
// 💰 COMPONENTE DE AHORROS MODERNO: src/components/AGTFin/dashboard/savings/FinSavings.js
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  LinearProgress,
  Grid,
  Chip,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Add as AddIcon,
  TrendingUp,
  TrackChanges as Target,
  CalendarToday as CalendarTodayIcon,
  MoreVert,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Savings,
  CheckCircle,
  PauseCircle,
  PlayArrow,
  Remove,
  Security,
  Flight,
  DirectionsCar,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import agtFinService from '../../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES MODERNA (IGUAL QUE BILLS)
// ===========================================
const getGrayscalePalette = (theme) => ({
  // Colores principales en escala de grises
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  
  // Gradientes para elementos visuales
  gradients: {
    primary: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    
    tertiary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #BDBDBD 0%, #9E9E9E 100%)'
      : 'linear-gradient(180deg, #616161 0%, #757575 100%)',
    
    card: theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,1) 100%)'
  },
  
  // Colores para estados de los ahorros
  status: {
    active: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
    completed: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    paused: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575'
  },
  
  // Opacidades para diferentes elementos
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
    dark: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
    card: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'
  }
});

// ===========================================
// 🎯 COMPONENTES ESTILIZADOS MODERNOS (CORREGIDOS CON shouldForwardProp)
// ===========================================

// Contenedor principal moderno
const ModernContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0,0,0,0.2)' 
    : 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(255,255,255,0.1)' 
    : '1px solid rgba(0,0,0,0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 16,
  }
}));

// Tarjeta de resumen moderna
const SummaryCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.4)'
        : '0 12px 40px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.primary,
    }
  };
});

// Tarjeta de ahorro moderna - CORREGIDA
const ModernSavingCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const colors = getGrayscalePalette(theme);
  
  const getStatusGradient = () => {
    switch (status) {
      case 'completed':
        return colors.gradients.primary;
      case 'active':
        return colors.gradients.secondary;
      case 'paused':
        return colors.gradients.tertiary;
      default:
        return colors.gradients.primary;
    }
  };

  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.2)'
      : '0 8px 32px rgba(0,0,0,0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: theme.spacing(2),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.3)'
        : '0 12px 40px rgba(0,0,0,0.12)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: getStatusGradient(),
    }
  };
});

// Botón moderno con estilo del dashboard
const ModernButton = styled(Button)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 16,
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    background: colors.gradients.primary,
    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    border: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(255,255,255,0.1)'
      : '0 4px 16px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(255,255,255,0.15)'
        : '0 8px 24px rgba(0,0,0,0.15)',
      background: colors.gradients.secondary,
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
    '&:disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
    }
  };
});

// Diálogo moderno
const ModernDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 24,
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(30,30,30,0.95)'
          : 'rgba(255,255,255,0.95)'),
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: isWallpaperMode
        ? '1px solid rgba(255, 255, 255, 0.2)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.05)'),
      boxShadow: 'none',
    }
  };
});

// Campo de texto moderno
const ModernTextField = styled(TextField)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.backgrounds.medium,
      },
      '&.Mui-focused': {
        background: colors.backgrounds.medium,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: 2,
        }
      },
      '& fieldset': {
        borderColor: colors.backgrounds.dark,
        transition: 'all 0.3s ease',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  };
});

// FormControl moderno
const ModernFormControl = styled(FormControl)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.backgrounds.medium,
      },
      '&.Mui-focused': {
        background: colors.backgrounds.medium,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: 2,
        }
      },
      '& fieldset': {
        borderColor: colors.backgrounds.dark,
        transition: 'all 0.3s ease',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  };
});

// Chip moderno con gradiente - CORREGIDO
const ModernChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'chiptype',
})(({ theme, chiptype }) => {
  const colors = getGrayscalePalette(theme);
  
  const getChipStyles = () => {
    switch (chiptype) {
      case 'completed':
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'active':
        return {
          background: colors.gradients.secondary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'paused':
        return {
          background: colors.gradients.tertiary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      default:
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
    }
  };

  return {
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '12px',
    height: 28,
    textTransform: 'uppercase',
    ...getChipStyles(),
    '& .MuiChip-icon': {
      color: 'inherit',
    }
  };
});

// Barra de progreso moderna
const ModernLinearProgress = styled(LinearProgress)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.backgrounds.medium,
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
      background: colors.gradients.primary,
    },
  };
});

// ===========================================
// 📊 SECCIÓN DE RESUMEN MEJORADA (IGUAL QUE BILLS)
// ===========================================
const SummarySection = ({ summary, userProfile, theme, t }) => {
  const colors = getGrayscalePalette(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  const summaryItems = [
    {
      title: t('agtfin.savings.totalGoals'),
      value: summary?.overall?.count || 0,
      description: summary?.overall?.count === 1 ? t('agtfin.savings.goal') : t('agtfin.savings.goals_plural'),
      icon: <Target />,
      gradient: colors.gradients.secondary,
      status: 'active'
    },
    {
      title: t('agtfin.savings.totalSaved'),
      value: summary?.overall?.totalSavings || 0,
      description: t('agtfin.savings.saved'),
      icon: <Savings />,
      gradient: colors.gradients.primary,
      status: 'completed',
      isCurrency: true
    },
    {
      title: t('agtfin.savings.totalGoal'),
      value: summary?.overall?.totalTargets || 0,
      description: t('agtfin.savings.target'),
      icon: <TrendingUpIcon />,
      gradient: colors.gradients.tertiary,
      status: 'paused',
      isCurrency: true
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
        {t('agtfin.savings.summary')}
      </Typography>
      
      <Grid container spacing={isMobile ? 2 : 3}>
        {summaryItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <SummaryCard>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    background: item.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon, { 
                      sx: { 
                        fontSize: 28, 
                        color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                      } 
                    })}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight={700} sx={{ color: colors.primary }}>
                      {item.isCurrency ? formatCurrency(item.value) : item.value}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {item.description}
                </Typography>
                {summary?.overall?.completionPercentage !== undefined && index === 2 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                      {t('agtfin.savings.overallProgress')} {Math.round(summary.overall.completionPercentage)}%
                    </Typography>
                    <ModernLinearProgress 
                      variant="determinate" 
                      value={Math.min(summary.overall.completionPercentage || 0, 100)} 
                    />
                  </Box>
                )}
              </CardContent>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Tipos de objetivos de ahorro
const getGoalTypes = (t) => ({
  emergency: {
    label: t('agtfin.savings.goalTypes.emergency.label'),
    icon: Security,
    color: '#9E9E9E',
    description: t('agtfin.savings.goalTypes.emergency.description')
  },
  vacation: {
    label: t('agtfin.savings.goalTypes.vacation.label'),
    icon: Flight,
    color: '#757575',
    description: t('agtfin.savings.goalTypes.vacation.description')
  },
  investment: {
    label: t('agtfin.savings.goalTypes.investment.label'),
    icon: TrendingUp,
    color: '#616161',
    description: t('agtfin.savings.goalTypes.investment.description')
  },
  purchase: {
    label: t('agtfin.savings.goalTypes.purchase.label'),
    icon: DirectionsCar,
    color: '#424242',
    description: t('agtfin.savings.goalTypes.purchase.description')
  },
  other: {
    label: t('agtfin.savings.goalTypes.other.label'),
    icon: Savings,
    color: '#BDBDBD',
    description: t('agtfin.savings.goalTypes.other.description')
  }
});

const getPriorities = (t) => ({
  low: { label: t('agtfin.savings.priorities.low'), color: '#BDBDBD' },
  medium: { label: t('agtfin.savings.priorities.medium'), color: '#757575' },
  high: { label: t('agtfin.savings.priorities.high'), color: '#424242' }
});

// ===========================================
// 💰 COMPONENTE PRINCIPAL MODERNO
// ===========================================
const FinSavings = ({ user, userProfile }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = getGrayscalePalette(theme);

  // Estados principales
  const [savings, setSavings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados de modales
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'add', 'subtract'
  const [selectedSaving, setSelectedSaving] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '',
    goalType: 'emergency',
    targetDate: '',
    description: '',
    priority: 'medium'
  });
  const [transactionData, setTransactionData] = useState({
    amount: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Cargar ahorros y resumen
  const loadSavingsData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔄 Cargando datos de ahorros para usuario:', user?.uid);
      
      if (!agtFinService || !agtFinService.apiClient) {
        throw new Error(t('agtfin.savings.messages.serviceNotAvailable'));
      }

      const [savingsResponse, summaryResponse] = await Promise.all([
        agtFinService.getSavings(),
        agtFinService.getSavingsSummary()
      ]);

      console.log('✅ Datos de ahorros recibidos:', { savingsResponse, summaryResponse });

      setSavings(savingsResponse.savings || []);
      setSummary(summaryResponse.summary || null);

    } catch (err) {
      console.error('❌ Error cargando datos de ahorros:', err);

      let errorMessage = t('agtfin.savings.messages.loadError');

      if (err.message.includes('apiClient')) {
        errorMessage = t('agtfin.savings.messages.serviceError');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.uid) {
      loadSavingsData();
    } else {
      setLoading(false);
      setError(t('agtfin.common.sessionExpired'));
    }
  }, [user, loadSavingsData]);

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('agtfin.savings.messages.nameRequired');
    }

    if (!formData.targetAmount) {
      errors.targetAmount = t('agtfin.savings.messages.targetAmountRequired');
    } else if (isNaN(formData.targetAmount) || parseFloat(formData.targetAmount) <= 0) {
      errors.targetAmount = t('agtfin.savings.messages.targetAmountPositive');
    }

    if (!formData.targetDate) {
      errors.targetDate = t('agtfin.savings.messages.invalidDeadline');
    }

    return errors;
  };

  // Dialog handlers
  const handleOpenDialog = (type, saving = null) => {
    setDialogType(type);
    setSelectedSaving(saving);
    
    if (type === 'edit' && saving) {
      setFormData({
        name: saving.name || '',
        targetAmount: saving.targetAmount?.toString() || '',
        currentAmount: saving.currentAmount?.toString() || '',
        goalType: saving.goalType || 'emergency',
        targetDate: saving.targetDate || '',
        description: saving.description || '',
        priority: saving.priority || 'medium'
      });
    } else if (type === 'create') {
      setFormData({
        name: '',
        targetAmount: '',
        currentAmount: '',
        goalType: 'emergency',
        targetDate: '',
        description: '',
        priority: 'medium'
      });
    } else {
      setTransactionData({ amount: '', description: '' });
    }
    
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSaving(null);
    setFormData({
      name: '',
      targetAmount: '',
      currentAmount: '',
      goalType: 'emergency',
      targetDate: '',
      description: '',
      priority: 'medium'
    });
    setTransactionData({ amount: '', description: '' });
    setFormErrors({});
  };

  // CRUD Operations
  const handleCreateSaving = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');
      
      const savingData = {
        ...formData,
        targetAmount: parseFloat(formData.targetAmount),
        currentAmount: formData.currentAmount ? parseFloat(formData.currentAmount) : 0
      };
      
      console.log('💾 Creando nueva meta de ahorro:', savingData);
      
      await agtFinService.createSaving(savingData);
      await loadSavingsData();
      handleCloseDialog();
      
      console.log('✅ Meta de ahorro creada exitosamente');
      
    } catch (err) {
      console.error('❌ Error creando meta de ahorro:', err);

      let errorMessage = t('agtfin.savings.messages.createError');

      if (err.response?.status === 400) {
        errorMessage = err.response.data?.error || t('agtfin.savings.messages.invalidData');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleUpdateSaving = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');

      const updateData = {
        ...formData,
        targetAmount: parseFloat(formData.targetAmount)
      };

      console.log('🔄 Actualizando meta de ahorro:', selectedSaving.savingId, updateData);

      await agtFinService.updateSaving(selectedSaving.savingId, updateData);
      await loadSavingsData();
      handleCloseDialog();

      console.log('✅ Meta de ahorro actualizada exitosamente');

    } catch (err) {
      console.error('❌ Error actualizando meta de ahorro:', err);

      let errorMessage = t('agtfin.savings.messages.updateError');

      if (err.response?.status === 400) {
        errorMessage = err.response.data?.error || t('agtfin.savings.messages.invalidData');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteSaving = async (savingId) => {
    if (!window.confirm(t('agtfin.savings.actions.confirmDelete'))) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('🗑️ Eliminando meta de ahorro:', savingId);
      
      await agtFinService.deleteSaving(savingId);
      await loadSavingsData();
      
      console.log('✅ Meta de ahorro eliminada exitosamente');
      
    } catch (err) {
      console.error('❌ Error eliminando meta de ahorro:', err);

      let errorMessage = t('agtfin.savings.messages.deleteError');

      if (err.response?.status === 404) {
        errorMessage = t('agtfin.savings.messages.notFound');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToSaving = async () => {
    try {
      if (!transactionData.amount || parseFloat(transactionData.amount) <= 0) {
        setError(t('agtfin.savings.messages.transactionAmountPositive'));
        return;
      }

      setSubmitLoading(true);
      setError('');

      await agtFinService.addToSaving(selectedSaving.savingId, {
        amount: parseFloat(transactionData.amount),
        description: transactionData.description
      });

      await loadSavingsData();
      handleCloseDialog();

      console.log('✅ Dinero agregado exitosamente');

    } catch (err) {
      console.error('❌ Error agregando dinero:', err);

      let errorMessage = t('agtfin.savings.messages.transactionError');

      if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubtractFromSaving = async () => {
    try {
      if (!transactionData.amount || parseFloat(transactionData.amount) <= 0) {
        setError(t('agtfin.savings.messages.transactionAmountPositive'));
        return;
      }

      if (parseFloat(transactionData.amount) > selectedSaving.currentAmount) {
        setError(t('agtfin.savings.messages.insufficientFunds'));
        return;
      }

      setSubmitLoading(true);
      setError('');

      await agtFinService.subtractFromSaving(selectedSaving.savingId, {
        amount: parseFloat(transactionData.amount),
        description: transactionData.description
      });

      await loadSavingsData();
      handleCloseDialog();
      
      console.log('✅ Dinero retirado exitosamente');
      
    } catch (err) {
      console.error('❌ Error retirando dinero:', err);
      
      let errorMessage = 'No se pudo retirar dinero de la meta.';
      
      if (err.response?.status === 401) {
        errorMessage = t('agtfin.common.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Menu handlers
  const handleMenuClick = (event, saving) => {
    setAnchorEl(event.currentTarget);
    setSelectedSaving(saving);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSaving(null);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo si existe
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleTransactionChange = (event) => {
    const { name, value } = event.target;
    setTransactionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  const calculateDaysToTarget = (targetDate) => {
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'completed';
      case 'paused': return 'paused';
      default: return 'active';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'paused': return PauseCircle;
      default: return PlayArrow;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return t('agtfin.savings.status.completed');
      case 'paused': return t('agtfin.savings.status.paused');
      default: return t('agtfin.savings.status.active');
    }
  };

  // Obtener ícono según tipo de objetivo
  const getGoalTypeIcon = (type) => {
    const goalTypes = getGoalTypes(t);
    const goalType = goalTypes[type];
    return goalType ? goalType.icon : Savings;
  };

  // Obtener nombre según tipo de objetivo
  const getGoalTypeName = (type) => {
    const goalTypes = getGoalTypes(t);
    const goalType = goalTypes[type];
    return goalType ? goalType.label : t('agtfin.savings.goalTypes.other.label');
  };

  // Mostrar spinner mientras carga
  if (loading && savings.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} sx={{ color: colors.primary }} />
        <Typography variant="body1" color="text.secondary">
          {t('agtfin.savings.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <ModernContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Encabezado moderno */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '12px',
            background: colors.gradients.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AnalyticsIcon sx={{ 
              fontSize: 24, 
              color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
            }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ color: colors.primary }}>
              {t('agtfin.savings.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
              {t('agtfin.savings.subtitle')}
            </Typography>
          </Box>
        </Box>
        <ModernButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
          disabled={submitLoading}
        >
          {t('agtfin.savings.new')}
        </ModernButton>
      </Box>

      {/* Mensaje de error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            fontWeight: 500
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Sección de resumen */}
      {summary && (
        <SummarySection
          summary={summary}
          userProfile={userProfile}
          theme={theme}
          t={t}
        />
      )}

      {/* Lista de metas de ahorro */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
          {t('agtfin.savings.title')}
        </Typography>
        
        {savings.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            background: colors.backgrounds.card,
            borderRadius: 4,
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)' 
              : '1px solid rgba(0,0,0,0.05)',
          }}>
            <Box sx={{
              p: 3,
              borderRadius: '50%',
              background: colors.backgrounds.light,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <Savings sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
            </Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('agtfin.savings.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('agtfin.savings.emptyDescription')}
            </Typography>
            <ModernButton
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              {t('agtfin.savings.createFirst')}
            </ModernButton>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {savings.map((saving) => {
              const GoalIcon = getGoalTypeIcon(saving.goalType);
              const StatusIcon = getStatusIcon(saving.status);
              const daysToTarget = calculateDaysToTarget(saving.targetDate);
              const status = getStatusColor(saving.status);
              const statusText = getStatusText(saving.status);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={saving.savingId}>
                  <ModernSavingCard status={status}>
                    <CardContent sx={{ p: 4 }}>
                      {/* Header */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between', 
                        mb: 3 
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            p: 1.5,
                            borderRadius: '12px',
                            background: colors.backgrounds.medium,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {React.createElement(GoalIcon, { 
                              sx: { fontSize: 20, color: colors.primary } 
                            })}
                          </Box>
                          <Box>
                            <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary }}>
                              {saving.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              {getGoalTypeName(saving.goalType)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <IconButton 
                          size="small"
                          onClick={(e) => handleMenuClick(e, saving)}
                          sx={{
                            borderRadius: 2,
                            background: colors.backgrounds.light,
                            '&:hover': {
                              background: colors.backgrounds.medium,
                            }
                          }}
                        >
                          <MoreVert fontSize="small" sx={{ color: colors.primary }} />
                        </IconButton>
                      </Box>

                      {/* Estado */}
                      <Box sx={{ mb: 3 }}>
                        <ModernChip 
                          icon={React.createElement(StatusIcon, { fontSize: 'small' })}
                          label={statusText} 
                          chiptype={status}
                          size="small"
                        />
                      </Box>

                      {/* Progreso */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1
                        }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('agtfin.savings.details.progress')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary }}>
                            {Math.round(saving.progress || 0)}%
                          </Typography>
                        </Box>
                        <ModernLinearProgress
                          variant="determinate"
                          value={Math.min(saving.progress || 0, 100)}
                        />
                      </Box>

                      {/* Montos */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('agtfin.savings.saved')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary }}>
                            {formatCurrency(saving.currentAmount || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('agtfin.savings.target')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary }}>
                            {formatCurrency(saving.targetAmount || 0)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('agtfin.savings.details.remaining')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: colors.secondary }}>
                            {formatCurrency((saving.targetAmount || 0) - (saving.currentAmount || 0))}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Fecha objetivo */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {daysToTarget > 0 ? t('agtfin.savings.details.daysRemaining', { days: daysToTarget }) : t('agtfin.savings.details.deadlinePassed', { days: 0 })}
                          </Typography>
                        </Box>
                      </Box>

                      {saving.priority && (
                        <Box sx={{ mb: 3 }}>
                          <ModernChip
                            label={`${t('agtfin.savings.form.priority')}: ${getPriorities(t)[saving.priority]?.label || t('agtfin.savings.priorities.medium')}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: colors.backgrounds.dark,
                              color: colors.secondary,
                              background: 'transparent'
                            }}
                          />
                        </Box>
                      )}

                      <Divider sx={{ my: 3, backgroundColor: colors.backgrounds.medium }} />

                      {/* Acciones */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <ModernButton
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => handleOpenDialog('add', saving)}
                          disabled={saving.status === 'completed' || submitLoading}
                          sx={{ flex: 1, fontSize: '12px', py: 1 }}
                        >
                          {t('agtfin.savings.transactions.deposit')}
                        </ModernButton>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Remove fontSize="small" />}
                          onClick={() => handleOpenDialog('subtract', saving)}
                          disabled={saving.status === 'completed' || (saving.currentAmount || 0) === 0 || submitLoading}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '12px',
                            py: 1,
                            borderColor: colors.backgrounds.dark,
                            color: colors.secondary,
                            '&:hover': {
                              borderColor: colors.primary,
                              background: colors.backgrounds.light
                            }
                          }}
                        >
                          {t('agtfin.savings.transactions.withdrawal')}
                        </Button>
                      </Box>
                    </CardContent>
                  </ModernSavingCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Menu de acciones */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: colors.backgrounds.card,
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)' 
              : '1px solid rgba(0,0,0,0.05)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.3)'
              : '0 8px 32px rgba(0,0,0,0.1)',
          }
        }}
      >
        <MenuItem onClick={() => {
          handleOpenDialog('edit', selectedSaving);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: colors.primary }} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight={500}>{t('agtfin.savings.actions.edit')}</Typography>
          </ListItemText>
        </MenuItem>
        <Divider sx={{ backgroundColor: colors.backgrounds.medium }} />
        <MenuItem
          onClick={() => {
            handleDeleteSaving(selectedSaving?.savingId);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: colors.tertiary }} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight={500} sx={{ color: colors.tertiary }}>
              {t('agtfin.savings.actions.delete')}
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Modal de crear/editar meta */}
      <ModernDialog 
        open={openDialog && (dialogType === 'create' || dialogType === 'edit')} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${colors.backgrounds.medium}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            p: 1,
            borderRadius: '8px',
            background: colors.gradients.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {dialogType === 'create' ? (
              <Savings sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            ) : (
              <EditIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            )}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {dialogType === 'create' ? t('agtfin.savings.form.create') : t('agtfin.savings.form.edit')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.form.name')}
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                placeholder={t('agtfin.savings.form.namePlaceholder')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.form.targetAmount')}
                name="targetAmount"
                type="number"
                value={formData.targetAmount}
                onChange={handleFormChange}
                error={!!formErrors.targetAmount}
                helperText={formErrors.targetAmount}
                InputProps={{
                  startAdornment: (
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ mr: 1, fontWeight: 600 }}
                    >
                      {userProfile?.currency === 'USD' ? '$' : 
                       userProfile?.currency === 'EUR' ? '€' : 
                       userProfile?.currency === 'CLP' ? '$' : 
                       userProfile?.currency || '$'}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            
            {dialogType === 'create' && (
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.savings.form.currentAmount')}
                  name="currentAmount"
                  type="number"
                  value={formData.currentAmount}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: (
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ mr: 1, fontWeight: 600 }}
                      >
                        {userProfile?.currency === 'USD' ? '$' : 
                         userProfile?.currency === 'EUR' ? '€' : 
                         userProfile?.currency === 'CLP' ? '$' : 
                         userProfile?.currency || '$'}
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <ModernFormControl fullWidth>
                <InputLabel>{t('agtfin.savings.form.goalType')}</InputLabel>
                <Select
                  name="goalType"
                  value={formData.goalType}
                  label={t('agtfin.savings.form.goalType')}
                  onChange={handleFormChange}
                >
                  {Object.entries(getGoalTypes(t)).map(([key, type]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {React.createElement(type.icon, { fontSize: 'small' })}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </ModernFormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.form.deadline')}
                name="targetDate"
                type="date"
                value={formData.targetDate}
                onChange={handleFormChange}
                error={!!formErrors.targetDate}
                helperText={formErrors.targetDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <ModernFormControl fullWidth>
                <InputLabel>{t('agtfin.savings.form.priority')}</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  label={t('agtfin.savings.form.priority')}
                  onChange={handleFormChange}
                >
                  {Object.entries(getPriorities(t)).map(([key, priority]) => (
                    <MenuItem key={key} value={key}>
                      <ModernChip
                        label={priority.label}
                        size="small"
                        chiptype="active"
                        sx={{ bgcolor: priority.color, color: 'white' }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </ModernFormControl>
            </Grid>

            <Grid item xs={12}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.form.description')}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={3}
                placeholder={t('agtfin.savings.form.descriptionPlaceholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          borderTop: `1px solid ${colors.backgrounds.medium}`,
          gap: 2
        }}>
          <Button
            onClick={handleCloseDialog}
            disabled={submitLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              color: colors.secondary,
              '&:hover': {
                background: colors.backgrounds.light
              }
            }}
          >
            {t('agtfin.savings.form.cancel')}
          </Button>
          <ModernButton
            onClick={dialogType === 'create' ? handleCreateSaving : handleUpdateSaving}
            disabled={!formData.name || !formData.targetAmount || !formData.targetDate || submitLoading}
            startIcon={submitLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              dialogType === 'create' ? <AddIcon fontSize="small" /> : <EditIcon fontSize="small" />
            )}
          >
            {submitLoading ? t('agtfin.savings.form.creating') : (dialogType === 'create' ? t('agtfin.savings.form.create') : t('agtfin.savings.form.update'))}
          </ModernButton>
        </DialogActions>
      </ModernDialog>

      {/* Modal de agregar/retirar dinero */}
      <ModernDialog 
        open={openDialog && (dialogType === 'add' || dialogType === 'subtract')} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${colors.backgrounds.medium}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            p: 1,
            borderRadius: '8px',
            background: colors.gradients.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {dialogType === 'add' ? (
              <TrendingUpIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            ) : (
              <TrendingDownIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            )}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {dialogType === 'add' ? t('agtfin.savings.transactions.title') : t('agtfin.savings.transactions.title')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.transactions.amount')}
                name="amount"
                type="number"
                value={transactionData.amount}
                onChange={handleTransactionChange}
                InputProps={{
                  startAdornment: (
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ mr: 1, fontWeight: 600 }}
                    >
                      {userProfile?.currency === 'USD' ? '$' : 
                       userProfile?.currency === 'EUR' ? '€' : 
                       userProfile?.currency === 'CLP' ? '$' : 
                       userProfile?.currency || '$'}
                    </Typography>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>
            
            <Grid item xs={12}>
              <ModernTextField
                fullWidth
                label={t('agtfin.savings.transactions.description')}
                name="description"
                value={transactionData.description}
                onChange={handleTransactionChange}
                multiline
                rows={2}
                placeholder={t('agtfin.savings.transactions.descriptionPlaceholder')}
              />
            </Grid>

            {selectedSaving && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 2,
                    background: colors.backgrounds.light,
                    border: `1px solid ${colors.backgrounds.medium}`,
                    '& .MuiAlert-icon': {
                      color: colors.primary
                    }
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {t('agtfin.savings.details.currentAmount')}: {formatCurrency(selectedSaving.currentAmount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('agtfin.savings.details.targetAmount')}: {formatCurrency(selectedSaving.targetAmount || 0)}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `1px solid ${colors.backgrounds.medium}`,
          gap: 2
        }}>
          <Button 
            onClick={handleCloseDialog}
            disabled={submitLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              color: colors.secondary,
              '&:hover': {
                background: colors.backgrounds.light
              }
            }}
          >
            {t('agtfin.savings.transactions.cancel')}
          </Button>
          <ModernButton
            onClick={dialogType === 'add' ? handleAddToSaving : handleSubtractFromSaving}
            disabled={!transactionData.amount || parseFloat(transactionData.amount) <= 0 || submitLoading}
            startIcon={submitLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              dialogType === 'add' ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />
            )}
          >
            {submitLoading ? t('agtfin.savings.transactions.adding') : (dialogType === 'add' ? t('agtfin.savings.transactions.deposit') : t('agtfin.savings.transactions.withdrawal'))}
          </ModernButton>
        </DialogActions>
      </ModernDialog>
    </ModernContainer>
  );
};

export default FinSavings;